import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { RefreshCw, ShoppingCart, CreditCard, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

type AbandonRecord = {
  basketId: number;
  userId: number;
  userEmail: string;
  userName: string | null;
  userRole: "guest" | "registered";
  userCreatedAt: string;
  platform: "mobile" | "web";
  storeName: string | null;
  storeId: number;
  fulfilmentType: string | null;
  items: Array<{ name: string; pricePence: number; quantity: number; optionsSummary?: string }>;
  estimatedSubtotalPence: number;
  basketUpdatedAt: string;
  abandonedAt: string | null;
  contactInfo: { name?: string; phone?: string; address?: string; postcode?: string } | null;
  status: "attempted" | "browsing" | "no_basket";
};

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
}

function guestId(email: string) {
  const m = email.match(/^guest_([a-f0-9-]{8})/);
  return m ? `Guest …${m[1]}` : email;
}

function displayName(r: AbandonRecord) {
  if (r.userRole === "guest") {
    return r.contactInfo?.name ?? guestId(r.userEmail);
  }
  return r.userName || r.userEmail;
}

function ContactLine({ info }: { info: AbandonRecord["contactInfo"] }) {
  if (!info) return null;
  const parts = [info.name, info.phone, info.postcode].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span className="text-xs bg-violet-100 text-violet-700 rounded-full px-2 py-0.5 font-semibold">
      {parts.join(" · ")}
    </span>
  );
}

function Row({ r }: { r: AbandonRecord }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button className="w-full text-left px-4 py-3 hover:bg-slate-50 transition" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-slate-800">{displayName(r)}</span>
              <span className="text-xs">{r.platform === "mobile" ? "📱" : "🌐"}</span>
              {r.status === "attempted"
                ? <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-semibold"><CreditCard size={10} /> Reached checkout</span>
                : r.status === "browsing"
                ? <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-semibold"><ShoppingCart size={10} /> Basket only</span>
                : <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5 font-semibold">No basket</span>
              }
              {r.storeName && <span className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{r.storeName}</span>}
              <ContactLine info={r.contactInfo} />
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {fmt(r.basketUpdatedAt)}
              {r.fulfilmentType && <span className="ml-2 capitalize">{r.fulfilmentType}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            {r.estimatedSubtotalPence > 0 && (
              <div className="font-semibold text-slate-600 text-sm">£{(r.estimatedSubtotalPence / 100).toFixed(2)}</div>
            )}
            <div className="text-slate-300 text-xs">{open ? "▲" : "▼"}</div>
          </div>
        </div>
      </button>

      {open && (
        <div className="bg-slate-50 border-t border-slate-100 px-4 py-3 space-y-2">
          {r.status === "attempted" && (
            <div className="flex items-center gap-2 text-xs text-amber-700">
              <CreditCard size={12} className="text-amber-500" />
              Reached Stripe at {fmt(r.abandonedAt)} — did not complete payment
            </div>
          )}
          {Array.isArray(r.items) && r.items.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Basket</div>
              {r.items.map((item, i) => (
                <div key={i} className="flex items-baseline justify-between text-xs text-slate-700">
                  <span>{item.quantity}× {item.name}{item.optionsSummary ? ` (${item.optionsSummary})` : ""}</span>
                  <span className="text-slate-400 ml-3">£{((item.pricePence * item.quantity) / 100).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs font-semibold text-slate-600 pt-1 border-t border-slate-200">
                <span>Subtotal</span>
                <span>£{(r.estimatedSubtotalPence / 100).toFixed(2)}</span>
              </div>
            </div>
          )}
          {r.contactInfo && Object.keys(r.contactInfo).length > 0 && (
            <div className="space-y-1 pt-1 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact captured</div>
              {r.contactInfo.name    && <div className="text-xs text-slate-700">👤 {r.contactInfo.name}</div>}
              {r.contactInfo.phone   && <div className="text-xs text-slate-700">📞 {r.contactInfo.phone}</div>}
              {r.contactInfo.postcode && <div className="text-xs text-slate-700">📍 {r.contactInfo.postcode}</div>}
              {r.contactInfo.address && <div className="text-xs text-slate-500 leading-relaxed">{r.contactInfo.address}</div>}
            </div>
          )}
          <div className="text-xs text-slate-400 font-mono pt-1">{r.userEmail}</div>
        </div>
      )}
    </div>
  );
}

function Section({ title, records, lostPence }: { title: string; records: AbandonRecord[]; lostPence: number }) {
  if (records.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-semibold text-slate-700 text-sm">{title} ({records.length})</h2>
        {lostPence > 0 && (
          <span className="text-xs font-semibold text-red-600">£{(lostPence / 100).toFixed(2)} est. lost</span>
        )}
      </div>
      <div className="divide-y divide-slate-50">
        {records.map((r) => <Row key={r.basketId} r={r} />)}
      </div>
    </div>
  );
}

export default function AdminGuestActivity() {
  const [records, setRecords] = useState<AbandonRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState("7d");

  const load = () => {
    setLoading(true);
    const days = since === "1d" ? 1 : since === "7d" ? 7 : 30;
    const sinceDate = new Date(Date.now() - days * 86400_000).toISOString();
    fetch(`/api/admin/guest-activity?since=${sinceDate}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d: { records: AbandonRecord[] }) => setRecords(d.records ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [since]);

  const guests     = records.filter((r) => r.userRole === "guest");
  const registered = records.filter((r) => r.userRole !== "guest");
  const attempted  = records.filter((r) => r.status === "attempted").length;
  const noBasket   = records.filter((r) => r.status === "no_basket").length;
  const totalLost  = records.reduce((s, r) => s + r.estimatedSubtotalPence, 0);
  const guestLost  = guests.reduce((s, r) => s + r.estimatedSubtotalPence, 0);
  const regLost    = registered.reduce((s, r) => s + r.estimatedSubtotalPence, 0);

  return (
    <AdminLayout current="/admin/guest-activity">
      <div className="max-w-5xl mx-auto space-y-4 px-0">

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Checkout Abandonment</h1>
            <p className="text-slate-500 text-xs mt-0.5">Everyone with a basket who didn't complete an order</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={since} onChange={(e) => setSince(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white">
              <option value="1d">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-slate-100 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1"><Users size={13} className="text-slate-400" /><span className="text-xs text-slate-500">Total abandoned</span></div>
            <div className="text-2xl font-bold text-slate-800">{records.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1"><CreditCard size={13} className="text-amber-400" /><span className="text-xs text-slate-500">Reached checkout</span></div>
            <div className="text-2xl font-bold text-amber-600">{attempted}</div>
            <div className="text-xs text-slate-400 mt-0.5">Stripe shown, not paid</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-3 sm:p-4">
            <div className="text-xs text-slate-500 mb-1">Est. lost revenue</div>
            <div className="text-2xl font-bold text-red-600">£{(totalLost / 100).toFixed(2)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-3 sm:p-4">
            <div className="text-xs text-slate-500 mb-1">No basket added</div>
            <div className="text-2xl font-bold text-slate-400">{noBasket}</div>
            <div className="text-xs text-slate-400 mt-0.5">📱 {records.filter((r) => r.platform === "mobile").length} · 🌐 {records.filter((r) => r.platform === "web").length}</div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-100 flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 flex flex-col items-center justify-center py-16 text-slate-400">
            <ShoppingCart size={28} className="mb-3 text-slate-300" />
            <p className="text-sm">No abandoned baskets in this period</p>
          </div>
        ) : (
          <>
            <Section title="👤 Guest users" records={guests} lostPence={guestLost} />
            <Section title="🔐 Registered users" records={registered} lostPence={regLost} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
